import React, { useState } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y } from 'swiper/modules';
import type SwiperCore from 'swiper';

import 'swiper/css';

import { IProps } from './HeroBannerCarouselBlock.interface';
import './HeroBannerCarouselBlock.styles.css';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, { ContentColumn } from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import Image from '@/elements/Image/Image.component';

const HeroBannerCarouselBlock: React.FC<IProps> = ({
  slides,
  className,
  title,
  ...blockWrapperProps
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const finalBlockWrapperProps = {
    ...blockWrapperProps,
    layout: 'hero' as const,
  };

  const handleSlideChange = (swiper: SwiperCore) => {
    setActiveIndex(swiper.realIndex);
  };

  return (
    <BlockWrapper
      className={clsx('heroBannerCarouselBlock', className)}
      {...finalBlockWrapperProps}
    >
      {/* Global hidden h1 for screen readers using the block title */}
      {title && <Heading level={1} className="sr-only" html={title} />}
      <div className="heroBannerCarouselBlock__swiperContainer"> {/* Added container for swiper + nav */}
        <Swiper
          modules={[A11y]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          className="heroBannerCarouselBlock__swiper"
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          a11y={{
            prevSlideMessage: 'Previous slide',
            nextSlideMessage: 'Next slide',
            paginationBulletMessage: 'Go to slide {{index}}',
          }}
        >
          {slides?.map((slide, index) => (
            <SwiperSlide key={`herobanner-slide-${index}`} className="heroBannerCarouselBlock__slide">
              {/* Background Image Container */}
              {slide.backgroundImage && (
                <div className="heroBannerCarouselBlock__slideBackground">
                  <Image className='heroBannerCarouselBlock__backgroundImage' {...slide.backgroundImage} rounded={true} />
                </div>
              )}

              {/* Content Overlay */}
              <ContentWrapper>
                <ContentColumn layout="two-thirds">
                  <div className="heroBannerCarouselBlock__slideContent">
                    {slide.backButton && (
                      <Button
                        {...slide.backButton}
                        icon='chevron-left'
                        iconPosition='before'
                        size="sm"
                        className="heroBannerBlock__backButton"
                      />
                    )}
                    {slide.title && (
                      <Heading level={2} className="heroBannerCarouselBlock__title" html={slide.title} />
                    )}
                    {slide.description && (
                      <Text className="heroBannerCarouselBlock__description" html={slide.description} />
                    )}
                    {slide.buttons && slide.buttons.length > 0 && (
                      <div className="heroBannerCarouselBlock__buttons">
                        {slide.buttons.map((buttonProps, btnIndex) => (
                          <Button key={`slide-buttons-${btnIndex}`} {...buttonProps} />
                        ))}
                      </div>
                    )}
                  </div>
                </ContentColumn>
                <ContentColumn /> {/* Empty column for two-thirds layout */}
              </ContentWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Custom Navigation Buttons with Text */}
      {slides && slides.length > 1 && (
        <div className="heroBannerCarouselBlock__navigation">
          {slides.map((slide, index) => (
            <button
              key={`slide-navigdation-${index}`}
              className={clsx(
                'heroBannerCarouselBlock__navButton',
                index === activeIndex && 'heroBannerCarouselBlock__navButton--active'
              )}
              onClick={() => swiperInstance?.slideToLoop(index)}
              aria-label={`Go to slide ${index + 1}: ${slide.title || 'Untitled'}`}
            >
              {/* Use slide title for button text */}
              {slide.label || `Slide ${index + 1}`}{/* Fallback text */}
            </button>
          ))}
        </div>
      )}
    </BlockWrapper>
  );
};

export default HeroBannerCarouselBlock;
export { HeroBannerCarouselBlock };
